import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Search, ReceiptText, Bot } from "lucide-react";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import { Button, Spinner } from "../components/ui/kit";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";
import { useUrlStav } from "../lib/navigace";
import { nahratDoklad } from "../lib/nahratDoklad";

const UHRADA = {
  UHRAZENO: { label: "Uhrazeno", cls: "bg-success-bg text-success" },
  CASTECNE: { label: "Částečně", cls: "bg-accent/10 text-accent" },
  NEUHRAZENO: { label: "Neuhrazeno", cls: "bg-inputbg text-sec" },
  NEPLATI_SE: { label: "Neplatí se", cls: "bg-inputbg text-muted" },
};
const SMER = { PRIJATA: "Přijatá", VYDANA: "Vydaná" };

/** Druh se ukazuje jen tam, kde to není obyčejná faktura. */
function druhText(d) {
  if (d.konecna) return "Konečná faktura · ";
  return d.druh && d.druh !== "FAKTURA" ? `${d.druh_nazev} · ` : "";
}

function fmtD(d) {
  if (!d) return "—";
  const [y, m, dd] = d.split("-");
  return dd ? `${dd}. ${m}. ${y}` : d;
}

export default function Doklady() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [smer, setSmer] = useUrlStav("smer", "");
  const [q, setQ] = useUrlStav("q", "");
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [ocrMsg, setOcrMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (smer === "BEZ_ZAKAZKY") params.bez_zakazky = 1;
    else if (smer) params.smer = smer;
    if (q) params.q = q;
    const { data } = await api.get("/api/doklady", { params });
    setData(data);
  }, [smer, q]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    // Vícestránkový doklad se fotí po stranách — všechny strany jdou do AI naráz.
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    setErr(""); setUploading(true);
    try {
      const fid = await nahratDoklad(files, setOcrMsg);
      nav(`/doklady/${fid}`);
    } catch (e2) {
      setErr(e2.response ? apiError(e2.response?.data?.detail) : e2.message);
    } finally {
      setUploading(false); setOcrMsg("");
    }
  };

  const items = data?.items || [];
  const souhrn = data?.souhrn || {};

  return (
    <div data-testid="doklady-page">
      <PageHeader eyebrow="Ekonomika" title="Doklady"
        subtitle="Přijaté i vydané faktury. Nahraj doklad a přečteme ho za tebe."
        actions={
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" multiple className="hidden"
              onChange={onFile} data-testid="doklad-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doklad-upload-btn">
              {uploading ? <Spinner /> : <><Upload size={15} /> Nahrát doklad</>}
            </Button>
          </div>
        } />

      {ocrMsg && (
        <div className="card px-5 py-4 mb-4 flex items-center gap-3 text-[13px] text-sec" data-testid="ocr-status">
          <Bot size={18} className="text-accent" /> {ocrMsg}
        </div>
      )}
      {err && <div className="card px-5 py-3 mb-4 text-[13px] text-danger" data-testid="doklad-error">{err}</div>}

      {/* souhrn tmavý blok */}
      <div className="rounded-card bg-ink text-white p-5 mb-4 grid grid-cols-3 gap-4" data-testid="doklady-souhrn">
        <div><div className="label-up text-white/50 mb-1">Přijaté doklady</div><div className="amount text-[18px] font-800">{fmtKc(souhrn.prijate || 0)} Kč</div></div>
        <div><div className="label-up text-white/50 mb-1">Vydané doklady</div><div className="amount text-[18px] font-800">{fmtKc(souhrn.vydane || 0)} Kč</div></div>
        <div><div className="label-up text-white/50 mb-1">Po splatnosti</div><div className="amount text-[18px] font-800 text-accent">{souhrn.po_splatnosti || 0}</div></div>
      </div>

      {souhrn.bez_zakazky_pocet > 0 && (
        <button onClick={() => setSmer("BEZ_ZAKAZKY")} data-testid="bez-zakazky-alert"
          className="card w-full text-left px-5 py-4 mb-4 flex items-center gap-3 hover:bg-inputbg">
          <span className="h-2 w-2 rounded-full bg-accent shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-700 text-ink">
              {souhrn.bez_zakazky_pocet} {souhrn.bez_zakazky_pocet === 1 ? "přijatý doklad" : "přijatých dokladů"} bez zakázky
            </span>
            <span className="block text-[12px] text-muted mt-0.5">
              <span className="amount">{fmtKc(souhrn.bez_zakazky_kc || 0)} Kč</span> nákladů, které se nepočítají do žádné zakázky.
            </span>
          </span>
          <span className="text-[12px] font-600 text-accent shrink-0">Přiřadit →</span>
        </button>
      )}

      {/* filtry */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {[["", "Vše"], ["PRIJATA", "Přijaté"], ["VYDANA", "Vydané"], ["BEZ_ZAKAZKY", "Bez zakázky"]].map(([v, l]) => (
          <button key={v} onClick={() => setSmer(v)} data-testid={`filter-smer-${v || "vse"}`}
            className={`filtr ${smer === v ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>{l}</button>
        ))}
        <div className="relative w-full md:w-auto md:ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat číslo, dodavatele, VS…"
            data-testid="doklad-search"
            className="field pl-8 md:h-9 w-full md:w-[260px]" />
        </div>
      </div>

      <div className="card overflow-hidden">
        {data === null ? (
          <div className="p-10 flex justify-center"><Spinner /></div>
        ) : items.length === 0 ? (
          <EmptyState icon="ReceiptText" title="Zatím žádné doklady" description="Nahraj přijatou fakturu a přiřaď náklady k zakázkám." />
        ) : items.map((d) => {
          // Štítky: na počítači vpravo v řádku, na telefonu pod názvem.
          const stitky = (
            <>
              {d.bez_zakazky && <span className="label-up px-1.5 py-0.5 rounded-chip bg-accent/10 text-accent shrink-0">bez zakázky</span>}
              {d.po_splatnosti && <span className="label-up px-1.5 py-0.5 rounded-chip bg-danger/10 text-danger shrink-0">po splatnosti</span>}
              <span className={`label-up px-2 py-1 rounded-pill shrink-0 ${(UHRADA[d.stav_uhrady] || UHRADA.NEUHRAZENO).cls}`}>
                {(UHRADA[d.stav_uhrady] || UHRADA.NEUHRAZENO).label}
              </span>
            </>
          );
          return (
          <button key={d.id} onClick={() => nav(`/doklady/${d.id}`)} data-testid={`doklad-${d.id}`}
            className="w-full text-left flex items-center gap-3 px-4 md:px-5 py-3 border-b border-line-softer last:border-0 hover:bg-inputbg">
            <ReceiptText size={16} className="text-muted shrink-0 hidden md:block" />
            <span className="w-[70px] shrink-0 label-up text-muted hidden md:block">{SMER[d.smer] || "—"}</span>
            <span className="min-w-0 flex-1">
              <span className="t-telo md:text-[13px] font-600 text-ink truncate block">{(d.protistrana || {}).nazev || "Neznámá protistrana"}</span>
              <span className="block t-meta md:text-[11.5px] text-muted truncate">
                {d.ocr_stav === "ZPRACOVAVA_SE" ? <span className="text-accent">🤖 Čtu doklad…</span>
                  : <><span className="md:hidden">{SMER[d.smer] || "—"} · </span>{druhText(d)}{d.cislo || "bez čísla"} · {fmtD(d.datum_vystaveni)}</>}
              </span>
              <span className="md:hidden flex flex-wrap items-center gap-1 mt-1.5">{stitky}</span>
            </span>
            <span className="md:w-[130px] text-right shrink-0 self-start md:self-center">
              <span className="amount t-telo md:text-[13px] font-700 text-ink block whitespace-nowrap">{fmtKc(d.castka_s_dph || 0)} Kč</span>
              {/* Částečně uhrazený doklad bez doplatku je k ničemu — kolik ještě dlužíme? */}
              {d.stav_uhrady === "CASTECNE" && (
                <span className="amount text-[11px] font-600 text-accent block whitespace-nowrap" data-testid={`zbyva-${d.id}`}>
                  zbývá {fmtKc(d.zbyva_uhradit ?? ((d.castka_s_dph || 0) - (d.zaplaceno || 0)))} Kč
                </span>
              )}
            </span>
            <span className="hidden md:contents">{stitky}</span>
          </button>
          );
        })}
      </div>
    </div>
  );
}
